import type { ListBuilderConfiguration } from '../types';
import {
	buildEcommerceContactColumn,
	buildEcommerceDateAndUserColumn,
} from './ecommerce-person-columns';

/**
 * Access Logs list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const ACCESS_LOGS_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'access-logs-crm-users',
			cacheTTL: 600000,
			name: 'CRM Users',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'date',
					headers: [
						{
							id: 'date-header',
							text: 'Date',
						},
					],
					fields: [
						{
							id: 'date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'date',
									objectTable: 'access_logs',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
								formatOptions: {
									dateFormat: 'datetime-short',
								},
								allowTextWrap: false,
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'access_logs',
							fieldName: 'date',
							direction: 'DESC',
						},
					],
					width: 160,
					minWidth: 160,
					maxWidth: 160,
					verticalAlignment: 'top',
				},
				{
					id: 'user',
					headers: [
						{
							id: 'user-header',
							text: 'User',
						},
						{
							id: 'user-break',
							text: '',
							displayStyle: 'line_break',
						},
						{
							id: 'user-email-header',
							text: 'Email',
						},
					],
					fields: [
						{
							id: 'user-name-field',
							dataConfig: {
								type: 'template',
								template: '{userFirstName} {userLastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-user',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'user-email-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'userEmail',
									objectTable: 'access_logs',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'activity',
					headers: [
						{
							id: 'activity-header',
							text: 'Activity',
						},
					],
					fields: [
						{
							id: 'activity-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'activityDescription',
									objectTable: 'access_logs',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
								allowTextWrap: true,
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'xlarge',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'device-location',
					headers: [
						{
							id: 'device-header',
							text: 'Device',
						},
						{
							id: 'device-break',
							text: '',
							displayStyle: 'line_break',
						},
						{
							id: 'location-header',
							text: 'IP',
						},
					],
					fields: [
						{
							id: 'device-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'parsedUserAgent.summary',
									objectTable: 'access_logs',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-device',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'ip-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'ipAddress',
									objectTable: 'access_logs',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
			],
			filterRules: {
				accessLogsUserType: {
					defaultValue: [
						{
							id: 'crm',
							label: 'CRM',
						},
					],
				},
			},
		},
		{
			id: 'access-logs-ecommerce-users',
			cacheTTL: 600000,
			name: 'Storefront Users',
			type: 'default',
			active: true,
			sortOrder: 1,
			// PD-17191 — a keyword search over the storefront user (name,
			// email, phone) on top of the shared filters.
			activeFilters: [
				'accessLogsSearch',
				'apiAccessLogsEndpoint',
				'apiAccessLogsUser',
				'accessLogsDateRange',
			],
			columns: [
				// PD-17191 — the user's name sits under the date, and a Contact
				// column shows the email over the phone.
				buildEcommerceDateAndUserColumn({
					objectTable: 'access_logs',
					nameTemplate: '{userFirstName} {userLastName}',
				}),
				buildEcommerceContactColumn({ objectTable: 'access_logs' }),
				{
					id: 'activity',
					headers: [
						{
							id: 'activity-header',
							text: 'Activity',
						},
					],
					fields: [
						{
							id: 'activity-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'activityDescription',
									objectTable: 'access_logs',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
								allowTextWrap: true,
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'xlarge',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'device-location',
					headers: [
						{
							id: 'device-header',
							text: 'Device',
						},
						{
							id: 'device-break',
							text: '',
							displayStyle: 'line_break',
						},
						{
							id: 'location-header',
							text: 'IP',
						},
					],
					fields: [
						{
							id: 'device-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'parsedUserAgent.summary',
									objectTable: 'access_logs',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-device',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'ip-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'ipAddress',
									objectTable: 'access_logs',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
			],
			filterRules: {
				accessLogsUserType: {
					defaultValue: [
						{
							id: 'ecommerce',
							label: 'Ecommerce',
						},
					],
				},
			},
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'apiAccessLogsEndpoint',
			'apiAccessLogsUser',
			'accessLogsDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/settings/access-logs',
			objectLabel: 'Activity Logs',
			objectTable: 'access_logs',
			selectedFields: [
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: 'userFirstName', fieldLabel: 'First Name' },
				{ fieldName: 'userLastName', fieldLabel: 'Last Name' },
				{ fieldName: 'userEmail', fieldLabel: 'Email' },
				{ fieldName: 'userPhone', fieldLabel: 'Phone' },
				{ fieldName: 'activityDescription', fieldLabel: 'Activity' },
				{ fieldName: 'parsedUserAgent.summary', fieldLabel: 'Device' },
				{ fieldName: 'ipAddress', fieldLabel: 'IP Address' },
			],
		},
	],
};
